import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row } from 'react-bootstrap';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from "react-router-dom";

import i18n from 'I18n';

import CustomAlert from '../base/CustomAlert';
import { createAction, enLocaleNumberFormatter } from 'utils';
import { checkIfExpired } from 'utils/CampaignUtil';
import QuantitySelector from './QuantitySelector';
import './TicketInfoSection.scss';
import TimeSlotPicker from './SlotPicker';
import OtherInfoSection from './OtherInfoSection';
import LoadMoreSpinner from "component/common/LoadMoreSpinner";
import CustomButton from 'component/base/CustomButton';
import { ReactComponent as LeftArrow } from 'assets/back-btn.svg';
import { COUPON_CONFIRM_BUTTON_STATUS } from 'models/BuyCoupon';
import { isNotSimplifiedNoWebapp } from "utils/UIConfigUtils";
import { openLoginPage } from "utils/SSOUtil"

const BUY_TICKET_STEPS = {
  STEP_INPUT_INFO: "STEP_INPUT_INFO",
  STEP_CONFIRM: "STEP_CONFIRM",
};

const TicketInfoSection = ({
  detailData,
  language,
  isLogin,
  confirmButtonStatus,
  couponIsUnavaiable,
  ticketCampaignModalLoading,
  ticketCampaignAdditionalData,
  eventRegistrationUserInfo,
  errorMessage,
  ignoreLogin,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const noLoginInfo = !isLogin && !ignoreLogin

  const buyTicketData = detailData || {};
  const timeSlots = ticketCampaignAdditionalData.tickets || [];

  const [step, setStep] = useState(BUY_TICKET_STEPS.STEP_INPUT_INFO);
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState(null);
  const selectedTimeSlotStock = useMemo(() => {
    if (!selectedTimeSlotId) {
      return null;
    };
    let stock = null;
    // eslint-disable-next-line no-unused-expressions
    timeSlots?.forEach(item => {
      if (item.id === selectedTimeSlotId) {
        stock = item.stockCount;
      };
    });
    return stock;
  }, [timeSlots, selectedTimeSlotId]);

  const [count, setCount] = useState(buyTicketData.limit === 0 ? 0 : 1);
  const [btnMinusDisable, setBtnMinusDisable] = useState(true);
  const [btnAddDisable, setBtnAddDisable] = useState(
    buyTicketData.limit === 0 ||
    buyTicketData.limit === 1 ||
    buyTicketData.leftStock <= 1
  );

  const [questionData, setQuestionData] = useState([]);

  const [nextButtonActive, setNextButtonActive] = useState(false);
  const [confirmButtonActive, setConfirmButtonActive] = useState(true);

  const [showAlert, setShowAlert] = useState(false);

  const isConfirm = step === BUY_TICKET_STEPS.STEP_CONFIRM;

  const hideAlert = () => {
    setShowAlert(false);
  };

  const goToMyTicketOrRegisterResult = () => {
    // not login; login nowebapp webpage
    if (!isLogin || !isNotSimplifiedNoWebapp() && isLogin ) {
      history.push(`/campaign/${buyTicketData.campaignID}/register_success`, {
        uid: eventRegistrationUserInfo.ssoUid,
        memberShipID: eventRegistrationUserInfo.membershipId,
      });
      return;
    }

    dispatch({
      type: 'myCoupon/changeTab',
      payload: { index: couponIsUnavaiable ? 1 : 0, isTicket: true }
    });
    history.push('/user/myTicket');
  }

  useEffect(() => {
    if (!isLogin && confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS
      || !isNotSimplifiedNoWebapp() && isLogin && confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS
      // nowebapp also go to qrcode
    ) {
      goToMyTicketOrRegisterResult()
      return;
    };

    if (
      confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS
      || confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.FAIL
    ) {
      setShowAlert(true);
    }
  }, [confirmButtonStatus])

  useEffect(() => {
    dispatch({ type: 'buyCoupon/resetCouponModal' });
    if (!ignoreLogin) {
      dispatch({
        type: 'users/updateState',
        payload: {
          eventRegistrationData: {},
          eventRegistrationUserInfo: {},
        }
      })
    };

    return () => {
      dispatch({ type: 'buyCoupon/resetCouponModal' });
      dispatch({
        type: 'campaignDetail/updateState',
        payload: {
          ticketCampaignAdditionalData: {},
        }
      })
      dispatch({
        type: 'users/updateState',
        payload: {
          eventRegistrationData: {},
        }
      })
    }
  }, []);

  useEffect(() => {
    if (!buyTicketData?.campaignID) {
      return;
    }
    if (noLoginInfo) {
      return;
    };

    setStep(BUY_TICKET_STEPS.STEP_INPUT_INFO);
    dispatch(
      createAction("campaignDetail/getTicketCampaignAdditionalData")({
        campaignID: buyTicketData?.campaignID,
      })
    );
  }, [noLoginInfo, language, buyTicketData?.campaignID])

  useEffect(() => {
    if (!selectedTimeSlotStock) {
      return;
    };
    let limit = selectedTimeSlotStock;
    if (buyTicketData.limit || buyTicketData.limit === 0) {
      limit = Math.min(selectedTimeSlotStock, buyTicketData.limit);
    };

    if (count >= limit) {
      setCount(limit);
      setBtnMinusDisable(limit <= 1);
      setBtnAddDisable(true);
    } else {
      setBtnMinusDisable(count <= 1);
      setBtnAddDisable(false);
    }
  }, [selectedTimeSlotStock]);

  useEffect(() => {
    setQuestionData(ticketCampaignAdditionalData.otherInfo || []);
  }, [ticketCampaignAdditionalData.otherInfo]);

  useEffect(() => {
    if (noLoginInfo) {
      setNextButtonActive(true);
      return;
    };
    // eslint-disable-next-line no-unused-expressions
    const questionNotFilled = questionData?.some(item => (
      !item.optional
      && !item.defaultValue
    ));
    setNextButtonActive(!!selectedTimeSlotId && !!count && !questionNotFilled);
  }, [
    noLoginInfo,
    selectedTimeSlotId,
    count,
    questionData,
  ]);

  const updateCount = (newCount) => {
    const addDisable = [
      buyTicketData.limit,
      buyTicketData.leftStock,
      selectedTimeSlotStock,
    ].some(limit => (limit || limit === 0) && newCount >= limit)

    setCount(newCount);
    setBtnMinusDisable(newCount <= 1);
    setBtnAddDisable(addDisable);
  };

  const countMinus = () => {
    if (count > 1) {
      const newCount = count - 1;
      updateCount(newCount);
    }
  };

  const countAdd = () => {
    const newCount = count + 1;
    updateCount(newCount);
  };

  const onTimeSlotSelect = (timeSlotId) => {
    setSelectedTimeSlotId((prevId) => {
      if (timeSlotId === prevId) {
        return null;
      }
      return timeSlotId;
    });
  };

  const backAction = () => {
    setStep(BUY_TICKET_STEPS.STEP_INPUT_INFO);
  };

  const nextAction = () => {
    if (noLoginInfo) {
      openLoginPage({
        locale: language || "en",
        redirectTo: `/campaign/${buyTicketData?.campaignID}`,
        isEventRegister: true,
        enableOtp: buyTicketData?.enableOtp,
      })
      return;
    }

    setStep(BUY_TICKET_STEPS.STEP_CONFIRM);
  };

  const onConfirmButtonPress = () => {
    if (confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS) {
      goToMyTicketOrRegisterResult();
      return;
    };

    setConfirmButtonActive(false);
    dispatch(
      createAction("campaignDetail/participateTicketCampaign")({
        campaignID: buyTicketData.campaignID,
        selectedTimeSlotId,
        count,
        questionData,
        afterAction: () => {
          setConfirmButtonActive(true);
        }
      })
    );
  };

  const buttonText = () => {
    if (confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS) {
      return i18n.t('myTicket');
    };
    if (isConfirm) {
      return i18n.t('coupon:Confirm');
    };
    return i18n.t('getTicket');
  };

  const alertLeftBtnText = confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS
    ? i18n.t('campaign_ok')
    : "";

  const alertLeftBtnAction = () => {
    if (confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS) {
      hideAlert();
      history.push(`/campaign/${buyTicketData?.campaignID}`);
    }
  };

  const alertRightBtn = confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS
    ? i18n.t('myTicket')
    : i18n.t('campaign_ok');

  const alertRightBtnAction = () => {
    if (confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS) {
      goToMyTicketOrRegisterResult();
    } else {
      dispatch({ type: 'buyCoupon/resetCouponModal' });
      hideAlert();
    }
  };

  if (ticketCampaignModalLoading) {
    return (
      <div className="ticket_info_section_loading">
        <LoadMoreSpinner />
      </div>
    )
  }

  return (
    <>
      {
        isConfirm && confirmButtonStatus !== COUPON_CONFIRM_BUTTON_STATUS.SUCCESS && <div
          className='tick_campaign_back_button_container'
          onClick={backAction}
        >
          <LeftArrow className="ticket_campaign_back_button" />
        </div>
      }
      <div className={`ticket_info_section_container ${noLoginInfo ? "ticket_info_section_container_none" : ""}`}>
        <TimeSlotPicker
          language={language}
          isConfirm={isConfirm}
          timeSlots={timeSlots}
          onTimeSlotSelect={onTimeSlotSelect}
          selectedTimeSlotId={selectedTimeSlotId}
        />

        <QuantitySelector
          language={language}
          isConfirm={isConfirm}
          count={count}
          btnMinusDisable={btnMinusDisable}
          btnAddDisable={btnAddDisable}
          countMinus={countMinus}
          countAdd={countAdd}
        />

        <OtherInfoSection
          language={language}
          showOtherInfo={buyTicketData.showOtherInfo}
          questionData={questionData}
          setQuestionData={setQuestionData}
          isConfirm={isConfirm}
        />
      </div>
      <div className={`ticket_info_button_container ${noLoginInfo ? "ticket_info_button_container_none" : ""}`}>
        <CustomButton
          className="ticket_info_button"
          disabled={isConfirm ? !confirmButtonActive : !nextButtonActive}
          text={buttonText()}
          btnClicked={isConfirm ? onConfirmButtonPress : nextAction}
        />
      </div>
      <CustomAlert
        show={showAlert}
        onHide={hideAlert}
        title={confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS ? i18n.t("successfull_redeemed") : null}
        message={
          confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.FAIL
            ? errorMessage || ''
            : ''
        }
        leftbtn={alertLeftBtnText}
        leftAction={alertLeftBtnAction}
        rightbtn={alertRightBtn}
        rightAction={alertRightBtnAction}
        isLoading={false}
      ></CustomAlert>
    </>
  );
}

const mapStateToProps = (state) => ({
  language: state.users.language,
  isLogin: state.users.isLogin,
  confirmButtonStatus: state.buyCoupon.confirmButtonStatus,
  couponIsUnavaiable: state.buyCoupon.couponIsUnavaiable,
  ticketCampaignModalLoading: state.campaignDetail.ticketCampaignModalLoading,
  ticketCampaignAdditionalData: state.campaignDetail.ticketCampaignAdditionalData,
  eventRegistrationUserInfo: state.users.eventRegistrationUserInfo,
  errorMessage: state.buyCoupon.errorMessage,
  ignoreLogin: state.users.eventRegistrationData.ignoreLogin,
});

export default connect(mapStateToProps)(TicketInfoSection);
